import { useDispatch, useSelector } from 'react-redux';

import { Switch } from '@interstate/components/Switch';
import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as rateTypeSelectors from '@makemydeal/dr-dash-store/selectors/rateTypeSelectors';
import { dealLockHooks } from '../../hooks';

import { Box } from '@interstate/components/Box';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents/Types/InterstateInputEventTypes';
import type { SwitchEventValue } from '@interstate/components/Switch/Types/switch';
import { Tooltip } from '@interstate/components/Tooltip';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { LEASE } from '@makemydeal/dr-platform-types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { FC, ReactElement } from 'react';
import { useCallback } from 'react';
import { PAYMENT_GRID, PAYMENT_GRID_REQUIRES_VEHICLE } from '../../constants';
import { StyledPaymentGridForDealCentralScreen } from './paymentGridToggle.styles';

interface PaymentGridToggleProps {
    currentOfferType: OfferType;
}

export const usePaymentGridToggle = ({ currentOfferType }: PaymentGridToggleProps) => {
    const dispatch = useDispatch();
    const paymentGridEnabled: boolean = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridToggle(state, currentOfferType)
    );
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const isDealCentralScreen = useSelector(dealerSelectors.isDealCentralExperience);
    const referenceRateType = useSelector(rateTypeSelectors.getReferenceRateType);
    const selectedTerm = useSelector(offerReduxSelectors.getSelectedTerm);
    const months = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const rateType = useSelector(offerSelectors.getRateTypeOverrideFromOfferTypeOrSelectedTerm);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const isPaymentGridEnabled = isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabled;
    const isLocked = dealLockHooks.useIsDealLocked();
    // US2062271: Lease/Balloon grids run on program-quotes, which needs a vehicle to match a lender program.
    const isGatedForQuoteWithoutVehicle = useSelector((state: StateTree) =>
        offerReduxSelectors.shouldGatePaymentGridForQuote(state, currentOfferType)
    );

    const handleUpdatePaymentGridToggle = useCallback(
        (checked: boolean) => {
            if (isFlexBuyPayment) {
                dispatch(offerReduxActionCreators.updateFlexBuyPaymentGridToggle(checked, CDL_USER_INTENT.PAYMENT_GRID_TOGGLED));
            } else {
                dispatch(
                    offerReduxActionCreators.updatePaymentGridToggle(checked, undefined, CDL_USER_INTENT.PAYMENT_GRID_TOGGLED)
                );
            }
        },
        [dispatch, isFlexBuyPayment]
    );

    const isChecked = isPaymentGridEnabled;

    const handleRateTypeToggle = (checked: boolean) => {
        if (checked) {
            dispatch(baseDealActionCreators.toggleRateType(referenceRateType, selectedTerm.id, currentOfferType, months));
        } else {
            dispatch(baseDealActionCreators.setReferenceRateType(rateType));
        }
    };

    const handleToggleClick = (e: InterstateOnChangeEvent<SwitchEventValue>) => {
        const checked = e.target.value === 'checked';
        if (isDealCentralScreen && currentOfferType === LEASE && referenceRateType && rateType !== referenceRateType) {
            handleRateTypeToggle(checked);
        }

        logNewRelicPageAction(`MV:PaymentGrid - Payment Grid toggle ${checked}`, {
            mvOfferType: currentOfferType,
            mvFieldEdited: 'mv:payment:payment-grid-toggle',
            mvFieldValue: checked,
            mvDealXgId: dealXgId
        });

        handleUpdatePaymentGridToggle(checked);
    };

    const id = 'payment-grid-toggle';
    const isToggleDisabledBase = isOnePayLease || isLocked || isGatedForQuoteWithoutVehicle;
    // A restored/persisted toggle must not render as on while gated.
    const isCheckedForDisplay = isChecked && !isGatedForQuoteWithoutVehicle;

    return {
        id,
        label: PAYMENT_GRID,
        isChecked: isCheckedForDisplay,
        isDisabled: isToggleDisabledBase,
        isDealCentralScreen,
        gatedReason: isGatedForQuoteWithoutVehicle ? PAYMENT_GRID_REQUIRES_VEHICLE : undefined,
        onToggle: handleToggleClick
    };
};

const PaymentGridToggle: FC<PaymentGridToggleProps> = ({ currentOfferType }) => {
    const { id, label, isChecked, isDisabled, isDealCentralScreen, gatedReason, onToggle } = usePaymentGridToggle({
        currentOfferType
    });

    /**
     * Explains a gated toggle on hover over the control itself.
     *
     * The Tooltip anchors to a wrapper rather than to the Switch: a disabled input receives no pointer
     * events, so a tooltip bound directly to it would never open. MUI puts `pointer-events: none` on the
     * disabled control, which lets hover fall through to this wrapper — so the whole label + switch area
     * becomes the hover target. `display: inline-flex` keeps the wrapper sized to its content so the
     * hoverable region matches what the user sees.
     */
    const withVehicleRequiredTooltip = (control: ReactElement): ReactElement =>
        gatedReason ? (
            <Tooltip size="large" position="bottom" toolTipContent={gatedReason}>
                <Box sx={{ display: 'inline-flex' }} data-testid={`${id}-vehicle-required-tooltip`}>
                    {control}
                </Box>
            </Tooltip>
        ) : (
            control
        );

    return isDealCentralScreen ? (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1.714rem',
                paddingTop: '4px',
                paddingBottom: '4px',
                paddingRight: '4px'
            }}
        >
            {withVehicleRequiredTooltip(
                <StyledPaymentGridForDealCentralScreen>
                    <Switch
                        name={id}
                        checked={isChecked}
                        label={label}
                        labelSx={{
                            paddingTop: '0px',
                            justifyContent: 'space-between'
                        }}
                        layout={'horizontal'}
                        onClick={onToggle}
                        disabled={isDisabled}
                        data-testid={id}
                        labelAlignment="left"
                        className="payment-grid-toggle"
                    />
                </StyledPaymentGridForDealCentralScreen>
            )}
        </Box>
    ) : (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: '16px' }}>
            {withVehicleRequiredTooltip(
                <Switch
                    name={id}
                    checked={isChecked}
                    label={label}
                    labelSx={{
                        width: 'unset'
                    }}
                    layout="vertical"
                    onClick={onToggle}
                    disabled={isDisabled}
                    data-testid={id}
                />
            )}
        </Box>
    );
};

export default PaymentGridToggle;
